import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { InboundFaxesQuery, InboxDto, TableSearchResult } from '@app/core/model';
import { NotificationService } from '@app/core/service';
import { InboundFaxesTableApiService } from '@app/core/service/inbound-faxes-table-api.service';
import { InboundFaxesTableModel } from '@app/shared/models';
import { BehaviorSubject, finalize, map } from 'rxjs';

@Injectable()
export class InboundFaxesService {
  private readonly inboundFaxesTableApiService = inject(InboundFaxesTableApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly ODATA_QUERY_KEY = 'InboundFaxes:Table:Query';

  public isLoadingTableData$ = new BehaviorSubject(true);
  public tableData$ = new BehaviorSubject<TableSearchResult<InboundFaxesTableModel>>({ items: [], count: 0 });

  public reload(inbox: InboxDto, showArchive = false): void {
    if (inbox) {
      this.isLoadingTableData$.next(true);

      const query: InboundFaxesQuery = JSON.parse(localStorage.getItem(this.ODATA_QUERY_KEY));
      query.isArchived = showArchive;
      this.inboundFaxesTableApiService
        .getTableByInboxId(inbox.inboxId, query)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(
            (response: TableSearchResult<InboundFaxesTableModel>): TableSearchResult<InboundFaxesTableModel> => ({
              items: response.items,
              count: response.count,
            })
          ),
          finalize(() => this.isLoadingTableData$.next(false))
        )
        .subscribe({
          next: (result) => this.tableData$.next(result),
          error: () => {
            this.tableData$.next({ items: [], count: 0 });
            this.notificationService.showError(
              'Unable to Load Faxes',
              'There was an issue loading the faxes. Please try again later.'
            );
          },
        });
    }
  }
}
